/**
 * ADR-175 D1 / D8 — the amendable fields' SCOPE, mirrored from radar-api's
 * registry (src/utils/amendableFields.js). The shared fixture
 * (__tests__/fixtures/amendableFieldScopes.json, byte-identical in both
 * repos) keeps the two tables equal.
 *
 *   'contract' — recorded on the one contract the dialog is on.
 *   'person'   — recorded on EVERY contract in force on the effective date
 *                (position, full name): each contract's counterparty sees
 *                the addendum on its own contract.
 *
 * The server decides where an amendment lands; this module only lets the
 * dialog say so before submit.
 */

export const AMENDMENT_SCOPES = Object.freeze({ CONTRACT: 'contract', PERSON: 'person' });

export const AMENDABLE_FIELD_SCOPES = Object.freeze({
  termEndDate: AMENDMENT_SCOPES.CONTRACT,
  contractCompanyId: AMENDMENT_SCOPES.CONTRACT,
  contractTypeId: AMENDMENT_SCOPES.CONTRACT,
  engagementModelId: AMENDMENT_SCOPES.CONTRACT,
  positionId: AMENDMENT_SCOPES.PERSON,
  fullName: AMENDMENT_SCOPES.PERSON
});

export const amendmentScopeOf = (field) => AMENDABLE_FIELD_SCOPES[field] || null;

export const isPersonScopedField = (field) => amendmentScopeOf(field) === AMENDMENT_SCOPES.PERSON;

const ymd = (value) => (value ? String(value).slice(0, 10) : null);

/**
 * The contracts in force on `effectiveYmd`, mirroring radar-api's
 * findContractsInForceOn: open, started by that date, not scheduled to end
 * before it; the primary first. Reads employee.contracts[] (ADR-156 §5).
 *
 * @param {Object[]} contracts - employee.contracts
 * @param {string} effectiveYmd - YYYY-MM-DD
 * @returns {Object[]}
 */
export const contractsInForceOn = (contracts, effectiveYmd) => {
  if (!Array.isArray(contracts) || !effectiveYmd) return [];
  const inForce = contracts.filter((contract) => {
    if (contract.endDate) return false; // closed: no longer amendable
    if (ymd(contract.startDate) && ymd(contract.startDate) > effectiveYmd) return false;
    if (ymd(contract.termEndDate) && ymd(contract.termEndDate) < effectiveYmd) return false;
    return true;
  });
  return [
    ...inForce.filter((contract) => contract.isPrimary),
    ...inForce.filter((contract) => !contract.isPrimary)
  ];
};

/** Mirror of Gate A's episode half: absent source = radar-owned. */
export const isRadarOwnedContract = (contract) => !contract?.source || contract.source === 'radar';

/** ADR-194 — a contract stamped by a sync (or an unknown source). */
export const isSyncOwnedContract = (contract) => !isRadarOwnedContract(contract);

/**
 * The fields the Amend Contract route accepts (mirror of radar-api's
 * ENABLED_AMENDABLE_FIELDS; position amends through the Positions view).
 */
export const ROUTE_AMENDABLE_FIELDS = Object.freeze([
  'termEndDate', 'engagementModelId', 'contractCompanyId', 'contractTypeId', 'fullName'
]);

/**
 * ADR-194 D7 — the fields the dialog may offer on THIS contract. The server
 * states them on each contracts[] entry (`amendableFields`, its Gate A
 * answer); without that field (an older payload) the old rule applies:
 * radar-owned → every route field, sync-owned → none.
 *
 * @param {Object|null} contract - an employee.contracts[] entry
 * @returns {string[]}
 */
export const amendableFieldsOf = (contract) => {
  if (Array.isArray(contract?.amendableFields)) return contract.amendableFields;
  return isRadarOwnedContract(contract) ? [...ROUTE_AMENDABLE_FIELDS] : [];
};

// ADR-194 §12 — the contract's system of record (ADR-130 vocabulary), as
// the server states it on contracts[]; mirror of radar-api's
// systemOfRecordOfSource for an older payload.
const SYSTEM_OF_RECORD_BY_SOURCE = Object.freeze({
  radar: 'RADAR',
  powerbi: 'ENOVA',
  'americas-radar': 'AMERICAS'
});

export const systemOfRecordOf = (contract) => {
  if (contract && 'systemOfRecord' in contract) return contract.systemOfRecord;
  const source = contract?.source;
  if (!source) return 'RADAR';
  return SYSTEM_OF_RECORD_BY_SOURCE[source] ?? null;
};

/**
 * ADR-194 §12 — the CASL subject the ContractAmendment permission's
 * condition is evaluated against: the contract, never the org chart. The
 * server checks the same subject (plus the Edit rule) on every amendment
 * route.
 */
export const amendmentPermissionSubject = (contract) => ({
  __type: 'ContractAmendment',
  systemOfRecord: systemOfRecordOf(contract)
});

/**
 * ADR-194 D4 — may a person cancel this Pending amendment? Always on a
 * radar-owned contract (as before). On a sync-owned one: only an amendment a
 * person recorded, of fields radar records there — a sync-observed one is
 * corrected in the source system.
 */
export const canCancelAmendmentOn = (contract, event) => {
  if (isRadarOwnedContract(contract)) return true;
  if (event?.actorType !== 'User') return false;
  const fields = Object.keys(event?.payload?.changes || {});
  const allowed = amendableFieldsOf(contract);
  return fields.length > 0 && fields.every((field) => allowed.includes(field));
};

/**
 * ADR-188 D3 — the effective-date bounds of an amendment, mirroring the
 * server's assertEffectiveWithinContract: a CONTRACT fact takes effect while
 * its contract runs (on/after its start, on/before its scheduled end); the
 * default date is today, or the start of a contract that has not started yet.
 * A PERSON fact has no per-contract bound here — its reach (the contracts in
 * force on the date) is what the server checks, and personNotInForce says so.
 *
 * @param {Object} args
 * @param {Object|null} args.contract  the amended contract's contracts[] entry
 * @param {Object[]} args.contracts    employee.contracts (for a person fact)
 * @param {string} args.field          the amended field
 * @param {string|null} args.effectiveYmd
 * @param {string} args.todayYmd
 * @returns {{ minYmd: string|null, maxYmd: string|null, defaultYmd: string, problem: string|null }}
 */
export const amendmentEffectiveBounds = ({ contract, contracts = [], field, effectiveYmd = null, todayYmd, syncSystemLabel = null }) => {
  const startYmd = ymd(contract?.startDate);
  const endYmd = ymd(contract?.termEndDate);
  const defaultYmd = startYmd && startYmd > todayYmd ? startYmd : todayYmd;

  if (isPersonScopedField(field)) {
    // The server checks reach only when the person holds more than one open
    // contract (findContractsInForceOn returns null otherwise).
    const open = (contracts || []).filter((c) => !c.endDate);
    const notInForce = Boolean(contract && effectiveYmd && open.length > 1
      && !contractsInForceOn(contracts, effectiveYmd).some((c) => c.id === contract.id));
    return {
      minYmd: null,
      maxYmd: null,
      defaultYmd,
      problem: notInForce
        ? `This contract is not in force on ${effectiveYmd.split('-').reverse().join('/')} — record the change from a contract that is, or pick a date it runs on`
        : null
    };
  }

  let problem = null;
  if (effectiveYmd && startYmd && effectiveYmd < startYmd) {
    problem = `The contract starts on ${startYmd.split('-').reverse().join('/')} — the amendment cannot take effect before it`;
  } else if (effectiveYmd && endYmd && effectiveYmd > endYmd) {
    problem = `The contract is scheduled to end on ${endYmd.split('-').reverse().join('/')} — an amendment after it would never take effect`;
  } else if (syncSystemLabel && effectiveYmd && effectiveYmd > todayYmd) {
    // ADR-194 D6 — mirror of the server's SYNC_OWNED_AMENDMENT_SCHEDULED
    problem = `On a contract maintained by ${syncSystemLabel} an amendment takes effect today or earlier — a renewal in ${syncSystemLabel} would cancel a scheduled one`;
  }
  // ADR-194 D6 — a sync-owned contract takes no future date
  const maxYmd = syncSystemLabel
    ? (endYmd && endYmd < todayYmd ? endYmd : todayYmd)
    : endYmd;
  return { minYmd: startYmd, maxYmd, defaultYmd, problem };
};
